import React, { useState } from "react";
import axios from "axios";
import { MessageCircle } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { CONTACT } from "@/data/site";
import { Reveal, Kicker } from "./Reveal";
import { waUrl } from "@/lib/wa";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", people: "", date: "", time: "", message: "" });
  const [status, setStatus] = useState("idle");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const buildMessage = () => {
    const L = t.form;
    let m = `${t.waMessage}\n`;
    m += `\n• ${L.name}: ${form.name}`;
    m += `\n• ${L.phone}: ${form.phone}`;
    if (form.people) m += `\n• ${L.people}: ${form.people}`;
    if (form.date) m += `\n• ${L.date}: ${form.date}`;
    if (form.time) m += `\n• ${L.time}: ${form.time}`;
    if (form.message) m += `\n• ${L.message}: ${form.message}`;
    return m;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    const url = waUrl(buildMessage());
    // Fire-and-forget: persist reservation, but never block WhatsApp redirect.
    axios.post(`${API}/reservations`, form).catch(() => {});
    setStatus("sent");
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => setStatus("idle"), 2500);
  };

  const inputCls =
    "w-full border-b border-[var(--gold)]/25 bg-transparent py-3 font-sans text-[15px] text-[var(--text)] placeholder:text-[var(--text-3)] outline-none transition-colors focus:border-[var(--gold)]";

  return (
    <section id="reserve" className="relative py-24 md:py-36" data-testid="contact-section">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-14 px-5 md:grid-cols-2 md:px-10">
        <div>
          <Kicker className="mb-5">{t.contactKicker}</Kicker>
          <h2 className="font-serif text-5xl leading-[0.95] tracking-tight text-[var(--text)] md:text-7xl">
            {t.contactTitle}
          </h2>
          <p className="mt-6 max-w-sm font-sans text-[15px] leading-relaxed text-[var(--text-2)]">
            {t.contactIntro}
          </p>
          <div className="mt-8 hairline" />
          <a
            href={`tel:${CONTACT.phoneTel}`}
            className="mt-8 block font-serif text-4xl italic text-[var(--gold)] transition-colors hover:text-[var(--gold-light)]"
            data-testid="contact-phone-link"
          >
            {CONTACT.phoneDisplay}
          </a>
        </div>

        <Reveal delay={0.1}>
          <form onSubmit={onSubmit} className="space-y-6" data-testid="reservation-form">
            <input
              required
              value={form.name}
              onChange={update("name")}
              placeholder={t.form.name}
              className={inputCls}
              data-testid="form-name"
            />
            <input
              required
              value={form.phone}
              onChange={update("phone")}
              placeholder={t.form.phone}
              type="tel"
              className={inputCls}
              data-testid="form-phone"
            />
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <input
                value={form.people}
                onChange={update("people")}
                placeholder={t.form.people}
                type="number"
                min="1"
                className={inputCls}
                data-testid="form-people"
              />
              <input
                value={form.date}
                onChange={update("date")}
                type="date"
                className={`${inputCls} [color-scheme:dark]`}
                data-testid="form-date"
              />
              <input
                value={form.time}
                onChange={update("time")}
                type="time"
                className={`${inputCls} [color-scheme:dark]`}
                data-testid="form-time"
              />
            </div>
            <textarea
              value={form.message}
              onChange={update("message")}
              placeholder={t.form.message}
              rows={3}
              className={`${inputCls} resize-none`}
              data-testid="form-message"
            />
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="form-submit"
              className="flex w-full items-center justify-center gap-3 bg-[var(--gold)] px-8 py-4 font-sans text-sm font-semibold tracking-wide text-[var(--bg)] transition-transform hover:scale-[1.01] disabled:opacity-70"
            >
              <MessageCircle size={18} />
              {status === "sent" ? t.form.sent : status === "sending" ? t.form.sending : t.form.submit}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
